import {
  loadDeviceKeyPairForPassword,
  hashPassword,
  generateAndUploadDeviceKey,
  TokenType,
} from 'encryption/encryption'
import { CSRFToken } from 'util/csrfToken'

/**
 * Interface defining the delegate methods for handling login-related events
 */
export interface LoginDelegate {
  /** Called when authentication fails */
  onAuthenticationError(response: Response): Promise<void>

  /** Called when authentication is successful, before device key loading */
  onAuthenticationComplete(email: string, password: string): Promise<void>

  /** Called when both authentication and device key loading are complete */
  onAuthenticationAndKeyLoadingComplete(email: string, password: string): Promise<void>

  /** Called when there's an error loading the device key */
  onDeviceKeyLoadingError(): Promise<void>

  /** Called when the device key has been successfully loaded */
  onDeviceKeyLoaded(): Promise<void>
}

/**
 * Interface for login credentials
 */
export interface LoginCredentials {
  email: string
  password: string
}

export interface OtpCredentials {
  otp_attempt: string
  trust_device: string
}

function isLoginCredentials(
  credentials: LoginCredentials | OtpCredentials,
): credentials is LoginCredentials {
  return 'email' in credentials
}

// This is a block-local variable and not accessible from outside the file
let attemptedPassword: string | undefined
let attemptedEmail: string | undefined

/**
 * Service responsible for handling the patient user login process.
 *
 * This service manages:
 * - Authentication with the server
 * - Device key loading and generation
 * - Handling of 2FA and encryption unlocking flows
 * - State management for retry attempts
 */
export class LoginService {
  delegate: LoginDelegate

  /**
   * Creates a new LoginService instance
   * @param delegate - Object implementing LoginDelegate interface to handle various login events
   */
  constructor(delegate: LoginDelegate) {
    this.delegate = delegate
  }

  /**
   * Initiates the login process with the provided credentials
   *
   * The login flow:
   * 1. Attempts authentication with the server
   * 2. If successful and encryption unlocking is required:
   *    - Loads or generates device key
   *    - Handles the encryption unlocking process
   * 3. If 2FA is required:
   *    - Stores credentials for later use
   *    - Redirects to 2FA flow
   *
   * @param credentials - The login credentials to use
   */
  async call(credentials: LoginCredentials | OtpCredentials) {
    sessionStorage.clear()

    const patientUser: Record<string, any> = {}
    if (isLoginCredentials(credentials)) {
      patientUser.email = credentials.email

      /*
        ######################################################
        # This feature is needed for C5 compliance for PSS-07
        ######################################################
        for C5 compliance, password must be hashed before sending to server. Don't change/remove password-hashing below.
      */
      patientUser.password = await hashPassword(credentials.email, credentials.password)
    } else {
      patientUser.otp_attempt = credentials.otp_attempt
      patientUser.trust_device = credentials.trust_device
    }

    const response = await fetch('/patient_users/sign_in', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': CSRFToken(),
      },
      body: JSON.stringify({ patient_user: patientUser }),
    })

    if (!response.ok) {
      await this.delegate.onAuthenticationError(response)
    }

    if (response.url.includes('/encryption/unlocking')) {
      const email = isLoginCredentials(credentials) ? credentials.email : attemptedEmail || ''
      const password = isLoginCredentials(credentials)
        ? credentials.password
        : attemptedPassword || ''

      await this.delegate.onAuthenticationComplete(email, password)

      if (!password || password.length === 0) {
        await this.delegate.onAuthenticationAndKeyLoadingComplete(email, password)
        await this.visitResponse(response)
        return
      }

      try {
        await this.loadDeviceKey(password)
      } catch (e) {
        await this.delegate.onDeviceKeyLoadingError()
        return
      }

      // Remove the stored password
      attemptedPassword = undefined

      await this.delegate.onDeviceKeyLoaded()

      await this.delegate.onAuthenticationAndKeyLoadingComplete(email, password)

      await this.visitResponse(response)
    } else {
      // This might either be an incorrect password or the account requires a second factor.
      // In any case, we store the password in a global variable so that we can load the device key later.
      // If the password is incorrect, it will be overwritten in the next attempt (it's incorrect anyway). No harm done.
      if (isLoginCredentials(credentials)) {
        attemptedPassword = credentials.password
        attemptedEmail = credentials.email
      }

      await this.visitResponse(response)
    }
  }

  private async visitResponse(response: Response) {
    Turbo.visit(response.url, {
      action: 'replace',
      // @ts-expect-error the types are not complete (they might be internal to Turbo?)
      shouldCacheSnapshot: false,
      response: {
        // hard-coded 200 status code to avoid Turbo from generating event handlers multiple times
        statusCode: 200,
        responseHTML: await response.clone().text(),
        redirected: response.redirected,
      },
    })
  }

  /**
   * Loads or generates a device key for the given password
   *
   * This is a helper method used internally by the login flow to ensure
   * the device key is available for encryption operations.
   *
   * @param password - The password to use for key loading/generation
   */
  async loadDeviceKey(password: string) {
    const deviceKey = await loadDeviceKeyPairForPassword(password)
    if (!deviceKey) {
      await generateAndUploadDeviceKey(password, TokenType.Password)
    }
  }
}
